import { CheckCircle2, FileText, Loader2, Upload, X } from "lucide-react";
import { useCallback, useRef, useState } from "react";
import { cn } from "../../lib/utils";
import { Alert, AlertDescription } from "../ui/alert";
import { Button } from "../ui/button";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "../ui/dialog";

interface IdVerificationModalProps {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	onUpload: (file: File) => Promise<void>;
	verificationStatus?: "pending" | "approved" | "rejected";
}

const ACCEPTED_FILE_TYPES = [
	"application/pdf",
	"image/jpeg",
	"image/jpg",
	"image/png",
];
const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB

export function IdVerificationModal({
	open,
	onOpenChange,
	onUpload,
	verificationStatus,
}: IdVerificationModalProps) {
	const [file, setFile] = useState<File | null>(null);
	const [dragActive, setDragActive] = useState(false);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [preview, setPreview] = useState<string | null>(null);
	const fileInputRef = useRef<HTMLInputElement>(null);

	const validateFile = (file: File): string | null => {
		if (!ACCEPTED_FILE_TYPES.includes(file.type)) {
			return "Invalid file type. Please upload a PDF, JPG, or PNG file.";
		}
		if (file.size > MAX_FILE_SIZE) {
			return "File size exceeds 10MB limit. Please upload a smaller file.";
		}
		return null;
	};

	const handleFileSelect = useCallback(
		(selectedFile: File) => {
			const validationError = validateFile(selectedFile);
			if (validationError) {
				setError(validationError);
				setFile(null);
				setPreview(null);
				return;
			}

			setError(null);
			setFile(selectedFile);

			// Create preview for images
			if (selectedFile.type.startsWith("image/")) {
				const reader = new FileReader();
				reader.onload = (e) => {
					setPreview(e.target?.result as string);
				};
				reader.readAsDataURL(selectedFile);
			} else {
				setPreview(null);
			}
		},
		[validateFile],
	);

	const handleDrag = useCallback((e: React.DragEvent) => {
		e.preventDefault();
		e.stopPropagation();
		if (e.type === "dragenter" || e.type === "dragover") {
			setDragActive(true);
		} else if (e.type === "dragleave") {
			setDragActive(false);
		}
	}, []);

	const handleDrop = useCallback(
		(e: React.DragEvent) => {
			e.preventDefault();
			e.stopPropagation();
			setDragActive(false);

			if (e.dataTransfer.files?.[0]) {
				handleFileSelect(e.dataTransfer.files[0]);
			}
		},
		[handleFileSelect],
	);

	const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		if (e.target.files?.[0]) {
			handleFileSelect(e.target.files[0]);
		}
	};

	const handleUpload = async () => {
		if (!file) {
			setError("Please select a file to upload");
			return;
		}

		setLoading(true);
		setError(null);

		try {
			await onUpload(file);
			setFile(null);
			setPreview(null);
			if (fileInputRef.current) {
				fileInputRef.current.value = "";
			}
		} catch (err) {
			setError(
				err instanceof Error
					? err.message
					: "Failed to upload ID. Please try again.",
			);
		} finally {
			setLoading(false);
		}
	};

	const handleRemoveFile = () => {
		setFile(null);
		setPreview(null);
		setError(null);
		if (fileInputRef.current) {
			fileInputRef.current.value = "";
		}
	};

	const handleClose = () => {
		if (!loading) {
			setFile(null);
			setPreview(null);
			setError(null);
			setDragActive(false);
			onOpenChange(false);
		}
	};

	return (
		<Dialog open={open} onOpenChange={handleClose}>
			<DialogContent className="sm:max-w-lg" data-nova-dialog="true">
				<DialogHeader>
					<DialogTitle>ID Verification Required</DialogTitle>
					<DialogDescription>
						Upload a government-issued ID to verify you are 18+ and create API
						keys for projects with NSFW models.
					</DialogDescription>
				</DialogHeader>

				<div className="space-y-4 py-4">
					{verificationStatus === "pending" && (
						<Alert>
							<CheckCircle2 className="h-4 w-4" />
							<AlertDescription>
								Your ID verification is pending review. You will be notified
								once it's approved (typically within 24-48 hours).
							</AlertDescription>
						</Alert>
					)}

					{verificationStatus !== "pending" && (
						<>
							<div
								onDragEnter={handleDrag}
								onDragLeave={handleDrag}
								onDragOver={handleDrag}
								onDrop={handleDrop}
								className={cn(
									"border-2 border-dashed rounded-lg p-8 text-center transition-colors",
									dragActive
										? "border-primary bg-primary/5"
										: "border-border bg-muted/30",
									file && "border-primary",
								)}
							>
								{preview ? (
									<div className="space-y-4">
										<img
											src={preview}
											alt="Preview"
											className="max-h-48 mx-auto rounded-lg border border-border"
										/>
										<div className="flex items-center justify-center gap-2">
											<FileText className="h-4 w-4 text-muted-foreground" />
											<span className="text-sm text-muted-foreground">
												{file?.name || "Unknown file"}
											</span>
											<Button
												variant="ghost"
												size="icon"
												className="h-6 w-6"
												onClick={handleRemoveFile}
											>
												<X className="h-4 w-4" />
											</Button>
										</div>
									</div>
								) : file ? (
									<div className="space-y-4">
										<FileText className="h-12 w-12 mx-auto text-muted-foreground" />
										<div className="flex items-center justify-center gap-2">
											<span className="text-sm font-medium">{file.name}</span>
											<Button
												variant="ghost"
												size="icon"
												className="h-6 w-6"
												onClick={handleRemoveFile}
											>
												<X className="h-4 w-4" />
											</Button>
										</div>
									</div>
								) : (
									<div className="space-y-4">
										<Upload className="h-12 w-12 mx-auto text-muted-foreground" />
										<div>
											<p className="text-sm font-medium mb-1">
												Drag and drop your ID here
											</p>
											<p className="text-xs text-muted-foreground mb-4">
												or click to browse
											</p>
											<Button
												type="button"
												variant="outline"
												onClick={() => fileInputRef.current?.click()}
											>
												Select File
											</Button>
										</div>
										<p className="text-xs text-muted-foreground">
											Accepted: PDF, JPG, PNG (max 10MB)
										</p>
									</div>
								)}
							</div>

							<input
								ref={fileInputRef}
								type="file"
								accept=".pdf,.jpg,.jpeg,.png"
								onChange={handleFileInputChange}
								className="hidden"
							/>
						</>
					)}

					{error && (
						<Alert variant="destructive">
							<AlertDescription>{error}</AlertDescription>
						</Alert>
					)}

					<div className="text-xs text-muted-foreground space-y-1">
						<p>
							<strong>Accepted documents:</strong> Driver's license, passport,
							or national ID
						</p>
						<p>
							Your ID will be reviewed within 24-48 hours. We take privacy
							seriously and only use this for age verification.
						</p>
					</div>
				</div>

				<DialogFooter>
					<Button
						variant="outline"
						onClick={handleClose}
						disabled={loading || verificationStatus === "pending"}
					>
						{verificationStatus === "pending" ? "Close" : "Cancel"}
					</Button>
					{verificationStatus !== "pending" && (
						<Button onClick={handleUpload} disabled={loading || !file}>
							{loading ? (
								<>
									<Loader2 className="h-4 w-4 mr-2 animate-spin" />
									Uploading...
								</>
							) : (
								"Upload ID"
							)}
						</Button>
					)}
				</DialogFooter>
			</DialogContent>
		</Dialog>
	);
}
